import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import dayjs from 'dayjs';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { DocumentType } from 'src/app/_enums/document-type.enum';
import {
  FULL_BUSINESS_INSURANCES,
  getInsuranceTypeLabel,
  InsuranceType,
  PERSONAL_INSURANCES,
} from 'src/app/_enums/insurance-type.enum';
import {
  getPeriodicityByInsuranceType,
  getPeriodicityLabel,
  getPeriodicityMultiplier,
  Periodicity,
} from 'src/app/_enums/proposal.enums';
import { getFileName } from 'src/app/_helpers/utils';
import { FileSizePipe } from 'src/app/_pipes/file-size.pipe';
import { ProposalService } from 'src/app/_services/proposal.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import {
  ProductDetails,
  ProposalDetails,
  UpdatePolicyRequest,
} from 'src/app/_types/proposals.type';
import { v4 } from 'uuid';

import { IconsModule } from '../../icons/icons.module';
import {
  ProductItemFormGroup,
  ProductSelectionComponent,
  ProductsFormGroupNewProposal,
} from '../add-proposal-modal/product-selection/product-selection.component';
import { PdfViewerModalComponent } from '../pdf-viewer-modal/pdf-viewer-modal.component';
import { SlideInModalComponent } from '../slide-in-modal/slide-in-modal.component';

@Component({
  selector: 'app-update-policy-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SlideInModalComponent,
    PdfViewerModalComponent,
    ProductSelectionComponent,
    IconsModule,
    FileSizePipe,
  ],
  templateUrl: './update-policy-modal.component.html',
})
export class UpdatePolicyModalComponent implements OnDestroy {
  @ViewChild('modal') modal!: SlideInModalComponent;

  @ViewChild('pdfViewer') viewer!: PdfViewerModalComponent;

  @ViewChild('productSelectionComponent')
  productSelectionComponent!: ProductSelectionComponent;

  @Output()
  policyUpdated = new EventEmitter<void>();

  formId = `updatePolicyForm-${v4()}`;

  submitted = false;

  selectedProposal: ProposalDetails | null = null;

  files: File[] = [];

  /**
   * The documents already on the policy, as the update request has to name them
   * again: it replaces the whole set rather than adding to it. `documentId` is
   * null for a document recorded before the store held them — the request takes
   * ids, so such a document cannot be kept and has to be uploaded again.
   */
  existingDocs: { url: string; documentId: number | null }[] = [];

  periodicitySubscription?: Subscription;

  productListSubscription?: Subscription;

  insuranceTypeSubscription?: Subscription;

  coversPeriodicitySubscription?: Subscription;

  Periodicity = Object.values(Periodicity);

  insuranceTypeOptions: InsuranceType[] = [];

  getPeriodicityLabel = getPeriodicityLabel;

  getInsuranceTypeLabel = getInsuranceTypeLabel;

  getFileName = getFileName;

  updatePolicyForm = this.fb.group({
    policyId: new FormControl('', [Validators.required]),
    insuranceType: new FormControl<InsuranceType | null>(null, [
      Validators.required,
    ]),
    startDate: new FormControl('', [Validators.required]),
    endDate: new FormControl('', [Validators.required]),
    offerSignatureDate: new FormControl<string | null>(null),
  });

  products: FormGroup<ProductsFormGroupNewProposal> = this.fb.group({
    partnerId: new FormControl<string | null>(null, []),
    categoryId: new FormControl<string | null>(null, [Validators.required]),
    subCategoryId: new FormControl<string | null>(null, [Validators.required]),
    categoryName: new FormControl<string | null>(null, [Validators.required]),
    categoryNameInFrench: new FormControl<string | null>(null, []),
    categoryLogo: new FormControl<string | null>(null, []),
    subCategoryName: new FormControl<string | null>(null, [
      Validators.required,
    ]),
    subCategoryNameInFrench: new FormControl<string | null>(null, []),
    product: new FormControl<string[]>([], [Validators.required]),
    agent: new FormControl<string | null>(null, []),
    periodicity: new FormControl<string | null>(null, [Validators.required]),
    premiumAmount: new FormControl(0, [Validators.required, Validators.min(0)]),
    totalAmount: new FormControl(0, [Validators.required, Validators.min(0)]),
    productList: this.fb.array<FormGroup<ProductItemFormGroup>>(
      [],
      [Validators.required]
    ),
    coversPeriodicity: new FormControl<Periodicity>(Periodicity.YEAR),
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly proposalService: ProposalService,
    private readonly userAuthService: UserAuthService,
    private readonly toastrService: ToastrService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.periodicitySubscription =
      this.products.controls.periodicity.valueChanges.subscribe(
        (periodicity) => {
          this.setPremiumAmount(
            this.products.controls.totalAmount.value ?? 0,
            periodicity as Periodicity
          );
        }
      );
    this.productListSubscription =
      this.products.controls.productList.valueChanges.subscribe(() =>
        this.setTotalAmount()
      );
    this.coversPeriodicitySubscription =
      this.products.controls.coversPeriodicity.valueChanges.subscribe(() =>
        this.setTotalAmount()
      );
    this.insuranceTypeSubscription =
      this.updatePolicyForm.controls.insuranceType.valueChanges.subscribe(
        (insuranceType) => {
          if (!insuranceType) {
            return;
          }
          // The categories available depend on the insurance type, so the previous
          // category/subcategory/covers selection can no longer be trusted.
          this.products.controls.categoryId.reset();
          this.products.controls.subCategoryId.reset();
          this.products.controls.categoryName.reset();
          this.products.controls.categoryNameInFrench.reset();
          this.products.controls.categoryLogo.reset();
          this.products.controls.subCategoryName.reset();
          this.products.controls.subCategoryNameInFrench.reset();
          this.products.controls.product.reset();
          this.productList.clear();
          this.products.controls.coversPeriodicity.setValue(
            getPeriodicityByInsuranceType(insuranceType)
          );
          this.productSelectionComponent?.resetProductSelection();
        }
      );
  }

  // The covers periodicity is the one the amounts on the document are expressed in, so the
  // total amount due is their sum brought to a year; the (payment) periodicity then splits
  // that total into the premium amount.
  private setTotalAmount(): void {
    const coversPeriodicity = this.products.controls.coversPeriodicity.value;
    const totalAmount =
      (this.productList.value ?? []).reduce((sum, product) => {
        const amount = Number(product.amount ?? 0);
        return sum + (Number.isNaN(amount) ? 0 : amount);
      }, 0) * getPeriodicityMultiplier(coversPeriodicity);

    this.products.controls.totalAmount.setValue(
      Number.parseFloat(totalAmount.toFixed(2)),
      {
        emitEvent: false,
      }
    );
    this.setPremiumAmount(
      totalAmount,
      this.products.controls.periodicity.value as Periodicity
    );
  }

  openModal(proposal?: ProposalDetails | null): void {
    if (!proposal?.proposalId) {
      return;
    }

    this.selectedProposal = proposal;
    this.submitted = false;
    this.files = [];
    this.existingDocs = (proposal.contractDoc ?? []).map((url, index) => ({
      url,
      documentId: proposal.contractDocIds?.[index] ?? null,
    }));
    this.productSelectionComponent?.resetProductSelection();
    this.productList.clear();
    const startDate = proposal.policyDetails?.startDate
      ? dayjs(proposal.policyDetails.startDate).format('YYYY-MM-DD')
      : '';
    const endDate = proposal.policyDetails?.endDate
      ? dayjs(proposal.policyDetails.endDate).format('YYYY-MM-DD')
      : '';
    const offerSignatureDate = proposal.offerSignatureDate
      ? dayjs(proposal.offerSignatureDate).format('YYYY-MM-DD')
      : null;
    const categoryDetails = proposal.policyDetails?.categoryDetails;
    const categoryName =
      categoryDetails?.categoryName ||
      categoryDetails?.categoryNameInFrench ||
      '';
    const categoryNameInFrench =
      categoryDetails?.categoryNameInFrench ||
      categoryDetails?.categoryName ||
      '';
    const subCategoryName =
      categoryDetails?.subCategoryName ||
      categoryDetails?.subCategoryNameInFrench ||
      '';
    const subCategoryNameInFrench =
      categoryDetails?.subCategoryNameInFrench ||
      categoryDetails?.subCategoryName ||
      '';

    this.insuranceTypeOptions = this.buildInsuranceTypeOptions(
      proposal.insuranceType
    );

    this.updatePolicyForm.reset(
      {
        policyId: proposal.policyDetails?.policyId ?? '',
        insuranceType: proposal.insuranceType ?? null,
        startDate,
        endDate,
        offerSignatureDate,
      },
      { emitEvent: false }
    );
    this.products.reset(
      {
        partnerId: proposal.partnerId ?? '',
        categoryId: categoryDetails?.categoryId ?? '',
        subCategoryId: categoryDetails?.subCategoryId ?? '',
        categoryName,
        categoryNameInFrench,
        categoryLogo: categoryDetails?.categoryLogo ?? '',
        subCategoryName,
        subCategoryNameInFrench,
        product: [],
        agent: proposal.agentDetails?.agentId ?? '',
        periodicity: proposal.policyDetails?.periodicity ?? '',
        premiumAmount: proposal.policyDetails?.premiumAmount ?? 0,
        totalAmount: proposal.policyDetails?.totalAmount ?? 0,
        coversPeriodicity:
          proposal.policyDetails?.coversPeriodicity ??
          getPeriodicityByInsuranceType(proposal.insuranceType),
      },
      { emitEvent: false }
    );

    const selectedProducts =
      proposal.policyDetails?.productDetails?.productList ?? [];
    selectedProducts.forEach((product) => {
      this.productList.push(this.createProductFormGroup(product));
    });
    this.products.controls.product.setValue(
      selectedProducts.map(
        (product) => product.productNameInFrench || product.productName
      ),
      { emitEvent: false }
    );

    this.modal.openModal();
    this.cdr.detectChanges();
    setTimeout(() => this.initializeProductSelection(), 0);
  }

  closeModal(): void {
    this.modal.closeModal(false);
  }

  /**
   * A policy can only be moved to another insurance type of the same family. Centralisation and
   * cancellation are never offered, as they are not insurance types a policy can be moved to,
   * which also makes both lists disjoint: the current insurance type identifies the family.
   */
  private buildInsuranceTypeOptions(
    currentInsuranceType?: InsuranceType
  ): InsuranceType[] {
    const isSelectable = (insuranceType: InsuranceType): boolean =>
      insuranceType !== InsuranceType.CENTRALISATION &&
      insuranceType !== InsuranceType.CANCELLATION;

    // FULL_BUSINESS_INSURANCES re-lists a few types already held by BUSINESS_INSURANCES.
    const businessOptions = [...new Set(FULL_BUSINESS_INSURANCES)].filter(
      isSelectable
    );
    const personalOptions = PERSONAL_INSURANCES.filter(isSelectable);

    const options =
      currentInsuranceType && businessOptions.includes(currentInsuranceType)
        ? businessOptions
        : personalOptions;

    if (currentInsuranceType && !options.includes(currentInsuranceType)) {
      return [currentInsuranceType, ...options];
    }
    return options;
  }

  onFileUpload(event: Event): void {
    const uploadedFiles: File[] = Array.from(
      (event.target as HTMLInputElement).files ?? []
    );
    uploadedFiles.forEach((file) => {
      const alreadyExists = this.files.some(
        (existingFile) =>
          existingFile.type === file.type &&
          existingFile.size === file.size &&
          existingFile.name === file.name
      );
      if (!alreadyExists) {
        this.files.push(file);
      }
    });
    (event.target as HTMLInputElement).value = '';
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
  }

  removeExistingDoc(index: number): void {
    this.existingDocs.splice(index, 1);
  }

  canSubmit(): boolean {
    return (
      !this.submitted &&
      this.updatePolicyForm.valid &&
      this.products.valid &&
      (this.files.length > 0 || this.existingDocs.length > 0) &&
      !!this.selectedProposal
    );
  }

  submitPolicyUpdate(): void {
    if (!this.canSubmit() || !this.selectedProposal) {
      return;
    }
    this.submitted = true;

    if (this.files.length > 0) {
      const memberId = this.selectedProposal.memberDetails?.memberId;
      const familyMemberId =
        this.selectedProposal.familyMemberDetails?.familyMemberId;

      if (!memberId || !familyMemberId) {
        this.toastrService.error(
          $localize`Impossible de téléverser les documents`
        );
        this.submitted = false;
        return;
      }

      this.userAuthService
        .uploadDocuments(
          this.files,
          memberId,
          familyMemberId,
          DocumentType.OTHER
        )
        .subscribe({
          next: (uploadedDocuments) =>
            this.savePolicy(
              uploadedDocuments.map((document) => document.documentId)
            ),
          error: () => {
            this.toastrService.error(
              $localize`Une erreur est survenue lors du téléchargement des documents`
            );
            this.submitted = false;
          },
        });
      return;
    }

    this.savePolicy();
  }

  viewFile(key: string): void {
    if (key.includes('https://')) {
      this.setFileUrl(key);
      return;
    }
    this.userAuthService.getPresignedUrl(key).subscribe((res) => {
      if (res.status === 'SUCCESS') {
        this.setFileUrl(res.data.preSignedUrl);
      }
    });
  }

  private setFileUrl(url: string): void {
    this.viewer.pdfFileUrl = url;
    this.viewer.openModal();
  }

  private savePolicy(uploadedDocumentIds?: number[]): void {
    if (!this.selectedProposal) {
      this.submitted = false;
      return;
    }

    if (!this.products.value.categoryId || !this.products.value.subCategoryId) {
      this.toastrService.error(
        $localize`Les détails de la police sont incomplets`
      );
      this.submitted = false;
      return;
    }

    const keptDocumentIds = this.existingDocs
      .map((document) => document.documentId)
      .filter((documentId): documentId is number => documentId !== null);
    const mergedDocs = [...keptDocumentIds, ...(uploadedDocumentIds ?? [])];
    if (mergedDocs.length === 0) {
      this.toastrService.error(
        $localize`Veuillez téléverser le document de la police`
      );
      this.submitted = false;
      return;
    }
    const startDate = this.updatePolicyForm.controls.startDate.value;
    const endDate = this.updatePolicyForm.controls.endDate.value;
    const offerSignatureDate =
      this.updatePolicyForm.controls.offerSignatureDate.value;
    if (!startDate || !endDate) {
      this.submitted = false;
      return;
    }

    const request: UpdatePolicyRequest = {
      proposalId: this.selectedProposal.proposalId,
      policyId: this.updatePolicyForm.controls.policyId.value ?? '',
      insuranceType:
        this.updatePolicyForm.controls.insuranceType.value ??
        this.selectedProposal.insuranceType,
      contractDoc: mergedDocs,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      offerSignatureDate: offerSignatureDate
        ? new Date(offerSignatureDate).toISOString()
        : null,
      periodicity: this.products.controls.periodicity.value ?? '',
      coversPeriodicity: this.products.controls.coversPeriodicity.value,
      premiumAmount: Number(this.products.controls.premiumAmount.value ?? 0),
      categoryDetails: {
        categoryId: this.products.value.categoryId ?? '',
        categoryName: this.products.value.categoryName ?? '',
        categoryNameInFrench: this.products.value.categoryNameInFrench ?? '',
        categoryLogo: this.products.value.categoryLogo ?? '',
        subCategoryId: this.products.value.subCategoryId ?? '',
        subCategoryName: this.products.value.subCategoryName ?? '',
        subCategoryNameInFrench:
          this.products.value.subCategoryNameInFrench ?? '',
        documents:
          this.selectedProposal.policyDetails?.categoryDetails?.documents ?? [],
      },
      productDetails: {
        productList: this.productList.value.map((product) => ({
          productId: product.productId,
          productName: product.productName,
          productNameInFrench: product.productNameInFrench,
          amount: Number(product.amount ?? 0),
          coverOrder: product.coverOrder,
          score: product.score,
          rootProductId: product.rootProductId,
          mainProductId: product.mainProductId,
        })),
      },
      totalAmount: Number(this.products.controls.totalAmount.value ?? 0),
    };

    this.proposalService.updatePolicy(request).subscribe({
      next: (result) => {
        this.submitted = false;
        if (result.status === 'SUCCESS') {
          this.toastrService.success($localize`Police mise à jour avec succès`);
          this.policyUpdated.emit();
          this.closeModal();
          return;
        }
        this.toastrService.error(
          $localize`Une erreur est survenue lors de la mise à jour`
        );
      },
      error: (err: any) => {
        this.submitted = false;
        this.toastrService.error(
          err?.error?.error?.clientErrorMessage ??
            $localize`Une erreur est survenue lors de la mise à jour`
        );
      },
    });
  }

  get productList(): FormArray<FormGroup<ProductItemFormGroup>> {
    return this.products.controls.productList;
  }

  private createProductFormGroup(
    product: ProductDetails
  ): FormGroup<ProductItemFormGroup> {
    const productName = product.productName || product.productNameInFrench;
    const productNameInFrench =
      product.productNameInFrench || product.productName;

    return this.fb.group({
      productId: this.fb.control(product.productId, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      displayProductName: this.fb.control(productNameInFrench, {
        nonNullable: true,
      }),
      productName: this.fb.control(productName, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      productNameInFrench: this.fb.control(productNameInFrench, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      coverOrder: this.fb.control(product.coverOrder),
      score: this.fb.control(product.score),
      rootProductId: this.fb.control(product.rootProductId),
      mainProductId: this.fb.control(product.mainProductId),
      benefits: this.fb.control(product.benefits),
      amount: this.fb.control(product.amount ?? 0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    }) as FormGroup<ProductItemFormGroup>;
  }

  private setPremiumAmount(totalAmount: number, periodicity: Periodicity) {
    let divider = 1;
    switch (periodicity) {
      case Periodicity.MONTH:
        divider = 12;
        break;
      case Periodicity.QUARTER:
        divider = 4;
        break;
      case Periodicity.SEMESTER:
        divider = 2;
        break;
    }
    const premiumAmount = totalAmount / divider;
    this.products.controls.premiumAmount.setValue(
      Number.parseFloat((premiumAmount || 0).toFixed(2)),
      { emitEvent: false }
    );
  }

  private initializeProductSelection() {
    if (!this.productSelectionComponent?.initializeFromFormGroup) {
      return;
    }
    this.productSelectionComponent.initializeFromFormGroup().subscribe({
      next: (success) => {
        if (!success) {
          this.toastrService.error(
            $localize`Erreur lors du chargement des produits`
          );
        }
      },
      error: () => {
        this.toastrService.error(
          $localize`Erreur lors du chargement des produits`
        );
      },
    });
  }

  ngOnDestroy(): void {
    this.periodicitySubscription?.unsubscribe();
    this.productListSubscription?.unsubscribe();
    this.coversPeriodicitySubscription?.unsubscribe();
    this.insuranceTypeSubscription?.unsubscribe();
  }
}
